/**
 * Back-in-stock "notify me when available" — storefront handler.
 *
 * Shown for sold-out variants (rendered by snippets/notify-when-available.liquid inside buy-buttons).
 * On submit it POSTs email + variant/product/SKU to the Shopify App Proxy endpoint (default
 * /apps/notify), served by a Cloudflare Worker that writes a `back_in_stock_request` metaobject.
 *
 * IMPLEMENTATION NOTE — why event delegation on `document`:
 * This theme's <product-rerender> REPLACES the whole <buy-buttons> subtree (replaceWith) on every
 * variant change. Per-element listeners attached in a custom element's connectedCallback are fragile
 * across those swaps and across script-load timing. A single delegated listener on `document` is
 * immune to all of that — it always catches clicks on the current button, however many times the
 * markup is re-rendered. No custom element / upgrade timing required.
 */
(function () {
  if (window.__notifyWhenAvailableBound) return;
  window.__notifyWhenAvailableBound = true;

  const SUBMIT_SELECTOR = '[data-notify-submit]';
  const ROOT_SELECTOR = 'notify-when-available';

  document.addEventListener('click', function (event) {
    const button = event.target.closest(SUBMIT_SELECTOR);
    if (!button) return;

    const root = button.closest(ROOT_SELECTOR);
    if (!root) return;

    event.preventDefault();
    event.stopPropagation();
    handleSubmit(root, button);
  });

  // Submit on Enter from the email field.
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    const input = event.target;
    if (!input.matches || !input.matches(`${ROOT_SELECTOR} [name="email"]`)) return;

    const root = input.closest(ROOT_SELECTOR);
    const button = root && root.querySelector(SUBMIT_SELECTOR);
    if (!root || !button) return;

    event.preventDefault();
    handleSubmit(root, button);
  });

  async function handleSubmit(root, button) {
    const emailInput = root.querySelector('[name="email"]');
    const email = (emailInput && emailInput.value || '').trim();

    if (!isValidEmail(email)) {
      showMessage(root, msg(root, 'invalid-email', 'Please enter a valid email address.'), 'error');
      emailInput && emailInput.focus();
      return;
    }

    setLoading(button, true);
    showMessage(root, '', null);

    const payload = {
      email: email,
      product_id: fieldValue(root, 'product_id'),
      variant_id: fieldValue(root, 'variant_id'),
      variant_sku: fieldValue(root, 'variant_sku'),
      product_title: fieldValue(root, 'product_title'),
      variant_title: fieldValue(root, 'variant_title'),
    };

    const proxyUrl = root.getAttribute('data-proxy-url') || '/apps/notify';

    try {
      const response = await fetch(proxyUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

      let data = {};
      try {
        data = await response.json();
      } catch (_) {
        /* non-JSON response — fall through to status check */
      }

      if (response.ok && data.ok !== false) {
        if (emailInput) emailInput.value = '';
        showMessage(root, msg(root, 'success', "Thanks! We'll email you when it's back in stock."), 'success');
      } else if (response.status === 409 || data.code === 'already_subscribed') {
        showMessage(root, msg(root, 'already-subscribed', "You're already on the list for this item."), 'success');
      } else {
        showMessage(root, msg(root, 'error', 'Something went wrong. Please try again.'), 'error');
      }
    } catch (_) {
      showMessage(root, msg(root, 'error', 'Something went wrong. Please try again.'), 'error');
    } finally {
      setLoading(button, false);
    }
  }

  function fieldValue(root, name) {
    const el = root.querySelector(`[name="${name}"]`);
    return el ? el.value : '';
  }

  function msg(root, key, fallback) {
    return root.getAttribute(`data-message-${key}`) || fallback;
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  function setLoading(button, isLoading) {
    if (!button) return;
    button.toggleAttribute('disabled', isLoading);
    button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  function showMessage(root, text, type) {
    const el = root.querySelector('.notify-when-available__message');
    if (!el) return;

    if (!text) {
      el.hidden = true;
      el.textContent = '';
      el.removeAttribute('data-type');
      return;
    }

    el.hidden = false;
    el.textContent = text;
    if (type) el.setAttribute('data-type', type);
    else el.removeAttribute('data-type');
  }
})();
